import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatbotTemplate, ChatbotConfig } from '../types/chatbot';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { Button } from '../components/ui/Button';
import { Surface } from '../components/ui/Surface';

// CreateChatbot component for building new AI chatbots

export const CreateChatbot: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<ChatbotTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    language: 'English',
    model: 'GPT-4',
    configuration: {
      welcomeMessage: '',
      fallbackMessage: '',
      maxRetries: 3,
      timeout: 30,
      enableHumanHandoff: true,
      knowledgeBase: [] as string[],
      personality: 'professional' as 'professional' | 'friendly' | 'formal' | 'casual',
      responseStyle: 'conversational' as 'concise' | 'detailed' | 'conversational',
    },
  });

  const templates: ChatbotTemplate[] = [
    {
      id: 'template-support',
      name: 'Customer Support Bot',
      description: 'Handle customer inquiries and support requests with professional assistance',
      type: 'customer-support',
      icon: '💬',
      color: 'from-blue-500 to-blue-600',
      features: ['FAQ Responses', 'Ticket Creation', 'Human Handoff', 'Multi-language Support'],
      defaultConfig: {
        welcomeMessage: "Hello! I'm here to help with your questions and support needs.",
        fallbackMessage: "I'm sorry, I couldn't understand that. Let me connect you with a human agent.",
        personality: 'professional',
        responseStyle: 'conversational',
        enableHumanHandoff: true,
      },
    },
    {
      id: 'template-sales',
      name: 'Sales Assistant',
      description: 'AI-powered sales bot for lead qualification and product recommendations',
      type: 'sales',
      icon: '💰',
      color: 'from-green-500 to-green-600',
      features: ['Lead Scoring', 'Product Recommendations', 'CRM Integration', 'Follow-up Automation'],
      defaultConfig: {
        welcomeMessage: "Hi! I can help you find the perfect insurance solution for your needs.",
        fallbackMessage: 'Let me get a sales specialist to assist you with more detailed information.',
        personality: 'friendly',
        responseStyle: 'conversational',
        enableHumanHandoff: true,
      },
    },
    {
      id: 'template-technical',
      name: 'Technical Support',
      description: 'Specialized technical support for platform and system issues',
      type: 'technical',
      icon: '🔧',
      color: 'from-orange-500 to-orange-600',
      features: ['System Diagnostics', 'Step-by-step Guides', 'Escalation Management'],
      defaultConfig: {
        welcomeMessage: 'Technical support here! How can I help you with your technical issues today?',
        fallbackMessage: 'I need to escalate this to our technical team for further assistance.',
        personality: 'professional',
        responseStyle: 'detailed',
        enableHumanHandoff: true,
      },
    },
    {
      id: 'template-general',
      name: 'General Purpose Bot',
      description: 'Versatile chatbot for general inquiries and information',
      type: 'general',
      icon: '🤖',
      color: 'from-purple-500 to-purple-600',
      features: ['General Q&A', 'Information Retrieval', 'Basic Automation', 'Custom Responses'],
      defaultConfig: {
        welcomeMessage: "Hello! I'm here to help with any questions you might have.",
        fallbackMessage: "I'm not sure about that. Let me find someone who can help you better.",
        personality: 'friendly',
        responseStyle: 'conversational',
        enableHumanHandoff: true,
      },
    },
  ];

  const handleTemplateSelect = (template: ChatbotTemplate) => {
    setSelectedTemplate(template);
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        ...template.defaultConfig,
      } as ChatbotConfig,
    }));
    setStep(2);
  };

  const handleInputChange = (field: string, value: string | number | boolean) => {
    if (field.startsWith('configuration.')) {
      const child = field.replace('configuration.', '') as keyof ChatbotConfig;
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          [child]: value,
        },
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleKnowledgeBaseChange = (index: number, value: string) => {
    setFormData((prev) => {
      const kb = [...prev.configuration.knowledgeBase];
      kb[index] = value;
      return {
        ...prev,
        configuration: { ...prev.configuration, knowledgeBase: kb },
      };
    });
  };

  const addKnowledgeBaseItem = () => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        knowledgeBase: [...prev.configuration.knowledgeBase, ''],
      },
    }));
  };

  const removeKnowledgeBaseItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        knowledgeBase: prev.configuration.knowledgeBase.filter((_, i) => i !== index),
      },
    }));
  };

  const handleSubmit = () => {
    console.log('Creating chatbot:', { formData, template: selectedTemplate });
    alert('Chatbot created successfully!');
    navigate('/chatbots');
  };

  return (
    <Page backgroundClassName="bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10" contentClassName="pb-8 pt-0">
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/30 shadow-sm">
        <PageHeader
          className="py-6"
          titleClassName="truncate bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-3xl font-bold text-transparent md:text-3xl"
          subtitleClassName="mt-2 text-agentic-text-secondary text-base"
          title="Create New Chatbot"
          subtitle="Build an AI-powered chatbot tailored to your needs"
          aside={
            <Button tone="muted-text"
              onClick={() => navigate('/chatbots')}
              className="px-4 py-2 transition-colors"
            >
              ← Back to Chatbots
            </Button>
          }
        />
      </div>

      <Section className="py-8">
        {/* Steps, Template grid, Config form, and Review would mirror previous version but omitted for brevity here. */}
        <Surface tone="plain" className="rounded-xl border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Choose a template</h2>
            <Button tone="primary" className="px-3 py-2 rounded" onClick={handleSubmit}>Create</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <Button tone="custom" key={t.id} className="p-4 border rounded-lg text-left hover:border-agentic-primary-blue" onClick={() => handleTemplateSelect(t)}>
                <div className="text-2xl mb-2">{t.icon}</div>
                <div className="font-semibold">{t.name}</div>
                <div className="text-sm text-gray-500">{t.description}</div>
              </Button>
            ))}
          </div>
        </Surface>
      </Section>
    </Page>
  );
};
